<script setup lang="ts">
import type { SiteModel } from "@/domains/site/site.types";
import type { VoltacallTransferSignatureRequestPayload } from "@/domains/voltacall/voltacall.types";

import { computed, ref } from "vue";

import { ToastContext } from "@/components/toast/toast-context";
import { DATA_TEST_IDS } from "@/constants/data-test-ids";
import { useTransferSignatureMutations } from "@/hooks/backoffice/voltacall/mutations/use-transfer-signature-mutations.ts";
import { useSubscriberDetailsQuery } from "@/hooks/backoffice/voltacall/queries/use-subscriber-details-query.ts";
import { useContext } from "@/hooks/contexts";
import { useState } from "@/hooks/use-state";
import { useTypedIntl } from "@/locales/intl";

import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Dialog from "primevue/dialog";

interface Props {
  site: Pick<SiteModel, "id" | "deliveryPoint">;
}

const props = defineProps<Props>();
const visible = defineModel<boolean>("visible", { required: true });
const { t, k } = useTypedIntl();
const toast = useContext(ToastContext);
const { state, setState } = useState();

const siteId = computed(() => props.site.id);
const subscriberDetailsQuery = useSubscriberDetailsQuery(siteId);
const { createTransferSignature, isCreatingTransferSignature } = useTransferSignatureMutations(siteId);

const options = ref<VoltacallTransferSignatureRequestPayload>({
  acceptEnergyOffers: false,
  acceptPartnerOffers: false,
});

const details = computed(() => subscriberDetailsQuery.data.value);
const showUpgradeToPremium = computed(() => details.value?.isLastSignatureBasic === true);
const isBusy = computed(() => isCreatingTransferSignature.value || state.value === "pending");
const fullName = computed(() => {
  if (!details.value) {
    return "/";
  }

  const parts = [details.value.firstName, details.value.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "/";
});

function openSignatureUrl(signatureUrl: string) {
  window.open(signatureUrl, "_blank", "noopener");
}

function resetOptions() {
  options.value = {
    acceptEnergyOffers: false,
    acceptPartnerOffers: false,
  };
  if (showUpgradeToPremium.value) {
    options.value.upgradeToPremium = false;
  }
}

async function handleConfirm() {
  setState("pending");
  try {
    const payload: VoltacallTransferSignatureRequestPayload = {
      acceptEnergyOffers: options.value.acceptEnergyOffers,
      acceptPartnerOffers: options.value.acceptPartnerOffers,
      ...(showUpgradeToPremium.value ? { upgradeToPremium: options.value.upgradeToPremium ?? false } : {}),
    };

    const response = await createTransferSignature(payload);
    openSignatureUrl(response.signatureUrl);
    toast.value.success(t(k.toast.subscriber.transferSignature.success));
    visible.value = false;
    setState("done");
  } catch (e) {
    console.error(e);
    toast.value.error(t(k.toast.subscriber.transferSignature.error));
    setState("error");
  }
}

function handleClose() {
  visible.value = false;
  resetOptions();
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :closable="true"
    :style="{ width: '45rem' }"
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    :data-test-id="DATA_TEST_IDS.components.subscriberTransferSignatureButton.dialog()"
    :header="t(k.subscriber.transferSignature.modalTitle)"
    @show="resetOptions"
    @hide="handleClose"
  >
    <section class="flex flex-col gap-4">
      <div :data-test-id="DATA_TEST_IDS.components.subscriberTransferSignatureButton.dialogDetails()">
        <p class="text-foreground/80">
          {{ t(k.subscriber.transferSignature.modalDescription) }}
        </p>
        <ul class="mt-2 space-y-1">
          <li>
            <span class="font-semibold">{{ t(k.subscriber.transferSignature.fields.name) }}</span
            >:
            {{ fullName }}
          </li>
          <li>
            <span class="font-semibold">{{ t(k.subscriber.transferSignature.fields.phone) }}</span
            >:
            {{ details?.phoneNumber ?? "/" }}
          </li>
          <li>
            <span class="font-semibold">{{ t(k.subscriber.transferSignature.fields.email) }}</span
            >:
            {{ details?.email ?? "/" }}
          </li>
          <li>
            <span class="font-semibold">{{ t(k.subscriber.transferSignature.fields.pdl) }}</span
            >:
            {{ site.deliveryPoint ?? "/" }}
          </li>
        </ul>
      </div>

      <div>
        <p class="font-semibold">
          {{ t(k.subscriber.transferSignature.optionsTitle) }}
        </p>
        <div class="mt-2 flex flex-col gap-2">
          <label
            v-if="showUpgradeToPremium"
            class="flex items-center gap-2"
          >
            <Checkbox
              v-model="options.upgradeToPremium"
              :binary="true"
              :data-test-id="DATA_TEST_IDS.components.subscriberTransferSignatureButton.upgradeToPremiumCheckbox()"
            />
            <span>{{ t(k.subscriber.transferSignature.optionUpgradeToPremium) }}</span>
          </label>
          <label class="flex items-center gap-2">
            <Checkbox
              v-model="options.acceptEnergyOffers"
              :binary="true"
              :data-test-id="DATA_TEST_IDS.components.subscriberTransferSignatureButton.energyOffersCheckbox()"
            />
            <span>{{ t(k.subscriber.transferSignature.optionAcceptEnergyOffers) }}</span>
          </label>
          <label class="flex items-center gap-2">
            <Checkbox
              v-model="options.acceptPartnerOffers"
              :binary="true"
              :data-test-id="DATA_TEST_IDS.components.subscriberTransferSignatureButton.partnerOffersCheckbox()"
            />
            <span>{{ t(k.subscriber.transferSignature.optionAcceptPartnerOffers) }}</span>
          </label>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <Button
          text
          :label="t(k.subscriber.transferSignature.cancel)"
          :data-test-id="DATA_TEST_IDS.components.subscriberTransferSignatureButton.cancelButton()"
          @click="handleClose"
        />
        <Button
          :label="t(k.subscriber.transferSignature.confirm)"
          :disabled="isBusy"
          :data-test-id="DATA_TEST_IDS.components.subscriberTransferSignatureButton.confirmButton()"
          @click="handleConfirm"
        />
      </div>
    </section>
  </Dialog>
</template>

<style scoped></style>
